/**
 * Whether this device should ring, from nothing but the call.
 *
 * The rules are calls-v1 §5.1 and every client reads them off the same
 * object: your own row's status and `ring_until`, and whether the call is
 * over. A ring that is derived rather than remembered stops on every device
 * the moment any of them answers, because every device is told the same row
 * changed. The first client remembered "an invitation arrived" and forgot to
 * forget it.
 */

import type { Call, CallDeviceKind, CallParticipant, UUID } from '@/api/types'

/** How long a server that sends no `ring_until` is taken to ring for. */
export const RING_WINDOW_MS = 60_000

/**
 * `ringing`: sound and the prompt. `ongoing`: the ring window has passed and
 * you are still invited, so a quiet line for as long as the call runs.
 * `elsewhere`: you are in it on a device. `none`: nothing to show.
 */
export type RingState = 'ringing' | 'ongoing' | 'elsewhere' | 'none'

/** An invitation this tab holds, and when it first heard of it. */
export interface Invitation {
  call: Call
  receivedAt: number
}

export function ownParticipant(call: Call, selfId: UUID | undefined): CallParticipant | null {
  if (selfId === undefined) return null
  return call.participants.find((participant) => participant.user?.id === selfId) ?? null
}

/**
 * When ringing stops, in epoch milliseconds: the server's `ring_until` when
 * it sends one. A server older than it ends an unanswered call on its own
 * after the same minute, so a minute from when this tab heard of the call is
 * the honest stand-in.
 */
export function ringDeadline(call: Call, selfId: UUID | undefined, receivedAt: number): number {
  const ringUntil = ownParticipant(call, selfId)?.ring_until
  if (typeof ringUntil === 'string') {
    const parsed = Date.parse(ringUntil)
    if (Number.isFinite(parsed)) return parsed
  }
  return receivedAt + RING_WINDOW_MS
}

export function ringState(call: Call, selfId: UUID | undefined, receivedAt: number, now: number): RingState {
  if (call.status === 'ended') return 'none'
  const mine = ownParticipant(call, selfId)
  if (mine === null) return 'none'
  if (mine.status === 'joined') return 'elsewhere'
  if (mine.status !== 'invited') return 'none'
  return now < ringDeadline(call, selfId, receivedAt) ? 'ringing' : 'ongoing'
}

/** The device your seat is on, when you have one in a call that is running. */
export function joinedDevice(call: Call, selfId: UUID | undefined): { deviceKind: CallDeviceKind | null } | null {
  if (call.status === 'ended') return null
  const mine = ownParticipant(call, selfId)
  if (mine === null || mine.status !== 'joined') return null
  return { deviceKind: mine.device_kind ?? null }
}

/**
 * What the held invitation becomes when a newer copy of some call arrives.
 * The same call is kept — with when it was first heard of — while there is
 * still something to ring for or show, and dropped when there is not. Another
 * call takes its place only when that one is ringing now: a quiet line gives
 * way to a ring, never the other way round. And the call this tab is already
 * in is never an invitation.
 */
export function nextInvitation(
  current: Invitation | null,
  call: Call,
  selfId: UUID | undefined,
  now: number,
  activeCallId: UUID | null,
): Invitation | null {
  const held = current !== null && current.call.id === call.id
  if (call.id === activeCallId) return held ? null : current

  const receivedAt = held ? current.receivedAt : now
  const state = ringState(call, selfId, receivedAt, now)
  if (state !== 'ringing' && state !== 'ongoing') return held ? null : current
  if (held || current === null || state === 'ringing') return { call, receivedAt }
  return current
}
